import {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useContext,
} from "react"
import { LocaleContext } from "contexts"
import { altchaUrl, altchaKey } from "../../utils/env"

interface ExtendedAltchaWidgetCSSProperties extends AltchaWidgetCSSProperties {
  "--altcha-color-active"?: string
  "--altcha-border-radius"?: string
  "--altcha-color-border"?: string
  "--altcha-color-border-focus"?: string
  "--altcha-color-text"?: string
  "--altcha-color-base"?: string
}

interface AltchaProps {
  auto?: AltchaWidgetOptions["auto"]
  onStateChange?: (ev: Event | CustomEvent) => void
  style?: ExtendedAltchaWidgetCSSProperties
}

const Altcha = forwardRef<{ value: string | null }, AltchaProps>(
  ({ auto, onStateChange, style }, ref) => {
    const widgetRef = useRef<AltchaWidget & AltchaWidgetMethods & HTMLElement>(
      null,
    )
    const [value, setValue] = useState<string | null>(null)
    const [isLoaded, setIsLoaded] = useState(false)
    const { locale } = useContext(LocaleContext)

    useImperativeHandle(ref, () => {
      return {
        get value() {
          return value
        },
      }
    }, [value])

    useEffect(() => {
      const isClientWithCustomElements =
        typeof window !== "undefined" &&
        typeof window.customElements !== "undefined"

      if (isClientWithCustomElements) {
        import("altcha")
          .then(() => {
            if (window.altchaI18n) {
              window.altchaI18n.set("fi", {
                ...window.altchaI18n.get("fi"),
                label: "Vahvista, että et ole robotti.",
                verified: "Vahvistettu",
                verifying: "Vahvistetaan...",
                error: "Vahvista, että et ole robotti.",
                expired: "Vahvistus vanhentunut. Yritä uudelleen.",
                verificationRequired: "Vahvista, että et ole robotti.",
              })

              window.altchaI18n.set("en", {
                ...window.altchaI18n.get("en"),
                label: "Confirm you are not a robot.",
                error: "Confirm you are not a robot.",
                verificationRequired: "Confirm you are not a robot.",
              })

              window.altchaI18n.set("sv", {
                ...window.altchaI18n.get("sv"),
                label: "Bekräfta att du inte är en robot.",
                verified: "Bekräftad",
                verifying: "Bekräftar...",
                error: "Bekräfta att du inte är en robot.",
                expired: "Verifieringen har gått ut. Försök igen.",
                verificationRequired: "Bekräfta att du inte är en robot.",
              })
            }

            setIsLoaded(true)
          })
          .catch((error: unknown) => {
            console.error("Failed to load ALTCHA widget:", error)
          })
      }
    }, [locale])

    useEffect(() => {
      const handleStateChange = (ev: Event | CustomEvent) => {
        if ("detail" in ev) {
          const { detail } = ev
          const isWidgetError = detail.state === "error"
          const isExpired = detail.state === "expired"

          if (isWidgetError) {
            console.error(`ALTCHA widget error: ${detail.error || "Unknown error"}`)
          } else if (isExpired) {
            console.warn("ALTCHA challenge expired")
          }

          setValue(detail.payload ?? null)
          onStateChange?.(ev)
        }
      }

      const handleError = (ev: Event) => {
        console.error("ALTCHA error event:", ev)
      }

      const { current } = widgetRef

      if (current && isLoaded) {
        current.addEventListener("statechange", handleStateChange)
        current.addEventListener("error", handleError)
        return () => {
          current.removeEventListener("statechange", handleStateChange)
          current.removeEventListener("error", handleError)
        }
      }
    }, [onStateChange, isLoaded, locale])

    return isLoaded ? (
      <div className="[&_.altcha]:w-max [&_.altcha]:max-w-none">
        <altcha-widget
          hidelogo
          hidefooter
          ref={widgetRef}
          auto={auto}
          style={style}
          delay={1000}
          language={locale.split("-")[0]}
          challengeurl={`${altchaUrl}/v1/challenge?apiKey=${altchaKey}`}
        />
      </div>
    ) : (
      null
    )
  },
)

Altcha.displayName = "Altcha"

export default Altcha
